import React, { useState } from 'react';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { ChartSimpleIcon, CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import { cn } from '@frontend/shadcn/lib/utils';
import { getSegmentName } from '@frontend/components/widgets/SocialProfile/utils';
import { EngagementRateSectionProps } from './hooks';

type TabType = 'all' | 'sponsored';

export const EngagementRateSection: React.FC<EngagementRateSectionProps> = ({
  isLoading,
  engagementData,
  engagementRatioSegment,
}) => {
  const [activeTab, setActiveTab] = useState<TabType>('all');

  const formatPercentage = (value: number | null) => {
    if (value === null) return '--';
    return `${(value * 100).toFixed(1)}%`;
  };

  const getBarHeight = (value: number | null, currentRate: number | null, currentBenchmark: number | null) => {
    if (value === null) return 8; // Very small height for null values
    if (value === 0) return 8; // Very small height for 0% values

    // Calculate height relative to the maximum of current rate and benchmark
    const maxValue = Math.max(currentRate || 0, currentBenchmark || 0);
    if (maxValue === 0) return 8; // If both are 0, show minimal height

    // Scale from 20px (minimum for non-zero) to 176px (maximum)
    const minHeight = 20;
    const maxHeight = 176;
    const normalizedHeight = (value / maxValue) * maxHeight;

    return Math.max(normalizedHeight, minHeight);
  };

  if (isLoading) {
    return (
      <div className="w-full self-stretch bg-white inline-flex flex-col justify-start items-center gap-3">
        <div className="self-stretch flex flex-col justify-start items-start">
          <div className="self-stretch inline-flex justify-start items-center gap-2">
            <div className="flex-1 flex justify-start items-center gap-2">
              <Skeleton className="w-6 h-6 rounded-lg" />
              <Skeleton className="h-4 w-32" />
              <Skeleton className="w-4 h-4" />
            </div>
            <Skeleton className="h-8 w-24 rounded-lg" />
          </div>
        </div>
        <div className="self-stretch flex flex-col justify-end items-center gap-2">
          <div className="self-stretch px-12 border-b border-grey-3 inline-flex justify-start items-end gap-6 overflow-hidden">
            <div className="flex-1 inline-flex flex-col justify-start items-center gap-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="self-stretch h-44 rounded-tl-2xl rounded-tr-2xl" />
            </div>
            <div className="flex-1 inline-flex flex-col justify-start items-center gap-2">
              <Skeleton className="h-4 w-12" />
              <Skeleton className="self-stretch h-44 rounded-tl-2xl rounded-tr-2xl" />
            </div>
          </div>
          <div className="self-stretch px-12 inline-flex justify-start items-center gap-6">
            <Skeleton className="flex-1 h-4" />
            <Skeleton className="flex-1 h-4" />
          </div>
        </div>
      </div>
    );
  }

  if (!engagementData.hasData) {
    return (
      <div className="w-full self-stretch bg-white inline-flex flex-col justify-start items-center gap-3 py-8">
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 relative bg-blue-5 rounded-lg flex justify-center items-center">
            <ChartSimpleIcon className="w-3 h-2.5 text-white" />
          </div>
          <div className="text-grey-6 text-sm font-medium leading-normal">Engagement Rate</div>
          <Tooltip>
            <TooltipTrigger>
              <div className="w-4 h-4 relative">
                <CircleInfoIcon className="w-4 h-4 text-grey-4" />
              </div>
            </TooltipTrigger>
            <TooltipContent>
              <p className="max-w-xs">
                Engagement rate shows how actively the audience interacts with content,
                calculated as total engagements divided by follower count.
              </p>
            </TooltipContent>
          </Tooltip>
        </div>
        <div className="text-grey-5 text-center py-8">
          No engagement rate data available
        </div>
      </div>
    );
  }

  const currentRate = activeTab === 'all' ? engagementData.allPostsRate : engagementData.sponsoredRate;
  const currentBenchmark = activeTab === 'all' ? engagementData.allPostsBenchmark : engagementData.sponsoredBenchmark;

  return (
    <div className="w-full self-stretch bg-white inline-flex flex-col justify-start items-center gap-3">
      <div className="self-stretch flex flex-col justify-start items-start">
        <div className="self-stretch inline-flex justify-start items-center gap-2">
          <div className="flex-1 flex justify-start items-center gap-2">
            <div className="w-6 h-6 relative bg-blue-5 rounded-lg flex justify-center items-center">
              <ChartSimpleIcon className="w-3 h-2.5 text-white" />
            </div>
            <div className="text-grey-6 text-sm font-medium leading-normal">Engagement Rate</div>
            <Tooltip>
              <TooltipTrigger>
                <div className="w-4 h-4 relative">
                  <CircleInfoIcon className="w-4 h-4 text-grey-4" />
                </div>
              </TooltipTrigger>
              <TooltipContent>
                <p className="max-w-xs">
                  Engagement rate shows how actively the audience interacts with content,
                  calculated as total engagements divided by follower count.
                </p>
              </TooltipContent>
            </Tooltip>
          </div>
          <div className="self-stretch p-0.5 bg-grey-3 rounded-lg flex justify-start items-center">
            <div
              className={cn(
                'self-stretch px-2 py-1 rounded-md inline-flex flex-col justify-center items-center gap-2 cursor-pointer',
                activeTab === 'all' ? 'bg-white' : ''
              )}
              onClick={() => setActiveTab('all')}
            >
              <div className="text-center text-grey-6 text-xs font-medium leading-tight">All</div>
            </div>
            <div
              className={cn(
                'self-stretch px-2 py-1 rounded-tr-lg rounded-br-lg inline-flex flex-col justify-center items-center gap-2 cursor-pointer',
                activeTab === 'sponsored' ? 'bg-white' : ''
              )}
              onClick={() => setActiveTab('sponsored')}
            >
              <div className="text-center text-grey-6 text-xs font-normal leading-tight">Sponsored</div>
            </div>
          </div>
        </div>
      </div>
      <div className="self-stretch flex flex-col justify-end items-center gap-2">
        <div className="self-stretch px-12 border-b border-grey-3 inline-flex justify-start items-end gap-6 overflow-hidden">
          <div className="flex-1 inline-flex flex-col justify-start items-center gap-2">
            <div className="w-48 rounded-lg flex flex-col justify-center items-center gap-2">
              <div className="inline-flex justify-start items-start gap-2">
                <div className="inline-flex flex-col justify-center items-start">
                  <div className="self-stretch text-center text-grey-6 text-sm font-medium leading-normal">
                    {formatPercentage(currentRate)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className="self-stretch rounded-tl-2xl rounded-tr-2xl transition-all duration-500 bg-blue-5"
              style={{
                height: `${getBarHeight(currentRate, currentRate, currentBenchmark)}px`
              }}
            />
          </div>
          <div className="flex-1 inline-flex flex-col justify-start items-center gap-2">
            <div className="w-48 rounded-lg flex flex-col justify-center items-center gap-2">
              <div className="inline-flex justify-start items-start gap-2">
                <div className="inline-flex flex-col justify-center items-start">
                  <div className="self-stretch text-center text-grey-5 text-sm font-normal leading-normal">
                    {formatPercentage(currentBenchmark)}
                  </div>
                </div>
              </div>
            </div>
            <div
              className="self-stretch rounded-tl-2xl rounded-tr-2xl transition-all duration-500 bg-grey-3"
              style={{
                height: `${getBarHeight(currentBenchmark, currentRate, currentBenchmark)}px`
              }}
            />
          </div>
        </div>
        <div className="self-stretch px-12 inline-flex justify-start items-center gap-6">
          <div className="flex-1 rounded-lg inline-flex flex-col justify-center items-center gap-2">
            <div className="inline-flex justify-start items-start gap-2">
              <div className="inline-flex flex-col justify-center items-start">
                <div className="self-stretch text-center text-grey-6 text-sm font-medium leading-normal">
                  @{engagementData.username}
                </div>
              </div>
            </div>
          </div>
          <div className="flex-1 rounded-lg inline-flex flex-col justify-center items-center gap-2">
            <div className="inline-flex justify-start items-start gap-2">
              <div className="inline-flex flex-col justify-center items-start">
                <div className="self-stretch text-center text-grey-5 text-sm font-normal leading-normal">
                  {getSegmentName(null, engagementRatioSegment)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
